import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { applyPOSMappingsToMenuPerformance } from '@/lib/applyTrainedMappings';
import { loadPOSItemsFromReports } from '@/lib/posSalesItemMapping';
import { PLATFORMS, normalizePlatform, platformDisplayName } from '@/lib/platformConstants';
import toastLogo from '@/assets/toast-logo.png';
import doordashLogo from '@/assets/doordash-logo.png';
import uberEatsLogo from '@/assets/uber-eats-logo.svg';

interface POSReportsStep4Props {
  onNext: () => void;
  onPrevious: () => void;
  platform?: string | null; // Optional - if not provided, shows summary for all platforms
  onComplete: () => void;
}

interface PlatformSummary {
  id: string;
  name: string;
  logo: string;
  salesUploaded: boolean;
  payoutsUploaded: boolean;
  quantitiesUploaded: boolean;
  itemsMapped: number;
  totalItems: number;
}

const PLATFORM_LOGOS: Record<string, string> = {
  toast: toastLogo,
  doordash: doordashLogo,
  ubereats: uberEatsLogo,
};

export const POSReportsStep4 = ({ onNext, onPrevious, platform, onComplete }: POSReportsStep4Props) => {
  const { toast } = useToast();
  const [platformSummaries, setPlatformSummaries] = useState<PlatformSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    loadSummary();
  }, [platform]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        setLoading(false);
        return;
      }

      // Get all active POS integrations or just the specified platform
      let platformsToLoad: string[] = [];
      
      if (platform) {
        platformsToLoad = [platform];
      } else {
        const { data: integrations, error: intError } = await supabase
          .from('company_integrations')
          .select('integration_type')
          .eq('company_id', companyId)
          .in('integration_type', [PLATFORMS.TOAST, PLATFORMS.DOORDASH, 'ubereats'])
          .eq('is_active', true);
        
        if (intError) throw intError;
        
        platformsToLoad = (integrations || []).map(i => i.integration_type);
      }

      // Load summary for each platform
      const summaries: PlatformSummary[] = [];
      
      for (const plat of platformsToLoad) {
        const logo = PLATFORM_LOGOS[plat];
        if (!logo) continue;

        // Normalize platform name to match how it's stored in database
        // Match the normalization used in POSReportsStep3Training (lowercase + trim)
        const normalizedPlatform = plat.toLowerCase().trim();

        const { data: reports } = await supabase
          .from('pos_report_uploads')
          .select('report_type')
          .eq('company_id', companyId)
          .eq('platform', normalizedPlatform)
          .eq('status', 'completed');

        // Count actual mappings from database - use normalized platform and check all valid mapping types
        // Also try case-insensitive matching in case platform is stored differently
        const { count: mappedCount, error: mappingError } = await supabase
          .from('pos_menu_item_mappings')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('platform', normalizedPlatform)
          .not('mapped_type', 'is', null)
          .neq('mapped_type', 'ignored');

        if (mappingError) {
          console.error(`Error counting mappings for ${normalizedPlatform}:`, mappingError);
        }

        // Use loadPOSItemsFromReports to get accurate total items count
        // This function handles all the different data formats properly
        let posItems: any[] = [];
        let totalItems = 0;
        try {
          posItems = await loadPOSItemsFromReports(companyId, normalizedPlatform);
          totalItems = posItems.length;
        } catch (itemsError) {
          console.error(`Error loading POS items for ${normalizedPlatform}:`, itemsError);
          // Fallback: try to get count from processed_data directly
          const { data: posItemsData } = await supabase
            .from('pos_report_uploads')
            .select('processed_data')
            .eq('company_id', companyId)
            .eq('platform', normalizedPlatform)
            .eq('report_type', 'item_quantities')
            .eq('status', 'completed')
            .order('created_at', { ascending: false })
            .limit(1);

          if (posItemsData && posItemsData.length > 0 && posItemsData[0]?.processed_data) {
            const processedData = posItemsData[0].processed_data as any;
            if (processedData.item_identifiers && Array.isArray(processedData.item_identifiers)) {
              totalItems = processedData.item_identifiers.length;
            } else if (processedData.raw_data && Array.isArray(processedData.raw_data)) {
              const uniqueItems = new Set<string>();
              processedData.raw_data.forEach((item: any) => {
                if (item.item_name) {
                  uniqueItems.add(item.item_name.toLowerCase().trim());
                }
              });
              totalItems = uniqueItems.size;
            }
          }
        }

        // Also try to get count from progress table as a fallback/verification
        const { data: progress } = await supabase
          .from('pos_training_progress')
          .select('*')
          .eq('company_id', companyId)
          .eq('platform', normalizedPlatform)
          .single();

        // Use mappedCount from database query, fallback to progress table if available
        // If we have items but no mappings count, use 0
        const itemsMapped = mappedCount ?? progress?.items_mapped ?? 0;
        
        // Use totalItems from loadPOSItemsFromReports, fallback to progress table
        const finalTotalItems = totalItems > 0 ? totalItems : (progress?.total_pos_items || 0);

        const payoutsUploaded = reports?.some(r => r.report_type === 'payouts') || false;
        const salesUploadedDirectly = reports?.some(r => r.report_type === 'sales') || false;
        // Backward compat: old combined upload was stored as 'payouts'
        const isDelivery =
          normalizePlatform(normalizedPlatform) === PLATFORMS.UBER ||
          normalizePlatform(normalizedPlatform) === PLATFORMS.DOORDASH;
        const salesUploaded = salesUploadedDirectly || (isDelivery && payoutsUploaded);

        summaries.push({
          id: normalizedPlatform,
          name: platformDisplayName(plat),
          logo,
          salesUploaded,
          payoutsUploaded,
          quantitiesUploaded: reports?.some(r => r.report_type === 'item_quantities') || false,
          itemsMapped,
          totalItems: finalTotalItems,
        });
      }

      setPlatformSummaries(summaries);
    } catch (error: any) {
      console.error('Error loading summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      setCompleting(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      // Mark all platforms as completed and apply mappings
      for (const summary of platformSummaries) {
        await supabase
          .from('pos_training_progress')
          .update({ status: 'completed' })
          .eq('company_id', companyId)
          .eq('platform', summary.id);

        // Apply trained mappings to any unmapped menu_performance records
        try {
          await applyPOSMappingsToMenuPerformance(companyId, summary.id);
        } catch (mappingError) {
          console.warn(`Non-critical: Could not apply mappings for ${summary.name}:`, mappingError);
        }
      }

      toast({
        title: 'Success',
        description: 'POS training completed. Mappings will be applied to future data.',
      });

      onComplete();
    } catch (error: any) {
      console.error('Error completing platforms:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete POS training',
        variant: 'destructive',
      });
    } finally {
      setCompleting(false);
    }
  };

  const getCompletionStatus = (summary: PlatformSummary) => {
    const allReportsUploaded = summary.salesUploaded && summary.payoutsUploaded && summary.quantitiesUploaded;
    // Items are considered mapped if:
    // 1. We have items and all are mapped (itemsMapped === totalItems)
    // 2. OR we have mappings but totalItems is 0 (which might happen if data structure is different)
    const allItemsMapped = summary.totalItems > 0 
      ? summary.itemsMapped === summary.totalItems 
      : summary.itemsMapped > 0; // If no total items but we have mappings, consider it mapped
    
    // Complete: All reports uploaded AND (all items mapped OR quantities report not required)
    if (allReportsUploaded && (allItemsMapped || summary.totalItems === 0)) {
      return { status: 'complete', color: 'bg-green-500' };
    } 
    // In Progress: Some work done (reports uploaded OR items mapped)
    const someReportsUploaded = summary.salesUploaded || summary.payoutsUploaded || summary.quantitiesUploaded;
    if (allReportsUploaded || summary.itemsMapped > 0 || someReportsUploaded) {
      return { status: 'partial', color: 'bg-amber-500' };
    }
    // Not Started: No reports uploaded and no items mapped
    return { status: 'incomplete', color: 'bg-gray-300' };
  };

  const getReportCountForPlatform = (s: PlatformSummary) => ({
    total: 3,
    uploaded: (s.salesUploaded ? 1 : 0) + (s.payoutsUploaded ? 1 : 0) + (s.quantitiesUploaded ? 1 : 0),
  });
  const totalReports = platformSummaries.reduce((sum, s) => sum + getReportCountForPlatform(s).total, 0);
  const uploadedReports = platformSummaries.reduce((sum, s) => sum + getReportCountForPlatform(s).uploaded, 0);
  const totalItems = platformSummaries.reduce((sum, s) => sum + s.totalItems, 0);
  const mappedItems = platformSummaries.reduce((sum, s) => sum + s.itemsMapped, 0);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="h-4 w-96" />
        </div>
        {[1, 2].map((i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Skeleton className="h-12 w-12 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (platformSummaries.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-2">Review & Complete</h3>
          <p className="text-sm text-muted-foreground">
            No POS platforms configured.
          </p>
        </div>
        <div className="flex justify-between">
          <Button variant="outline" onClick={onPrevious}>
            Previous Step
          </Button>
          <Button onClick={onComplete}>
            Complete
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Review & Complete POS Training</h3>
        <p className="text-sm text-muted-foreground">
          Review the uploaded reports and mapping summary for all platforms.
        </p>
      </div>

      {/* Overall Summary */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold">{uploadedReports}/{totalReports}</div>
              <div className="text-sm text-muted-foreground">Reports Uploaded</div>
            </div>
            <div>
              <div className="text-2xl font-bold">{mappedItems}/{totalItems}</div>
              <div className="text-sm text-muted-foreground">Items Mapped</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform-by-Platform Summary */}
      <div className="space-y-4">
        {platformSummaries.map((summary) => {
          const { status, color } = getCompletionStatus(summary);
          
          return (
            <Card key={summary.id}>
              <CardHeader className="py-4">
                <div className="flex items-center gap-4">
                  <img
                    src={summary.logo}
                    alt={summary.name}
                    className="h-10 w-10 object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <div className="flex-1">
                    <CardTitle className="text-base flex items-center gap-2">
                      {summary.name}
                      <Badge 
                        variant={status === 'complete' ? 'default' : 'outline'}
                        className={status === 'complete' ? 'bg-green-500' : status === 'partial' ? 'border-amber-500 text-amber-600' : ''}
                      >
                        {status === 'complete' ? 'Complete' : status === 'partial' ? 'In Progress' : 'Not Started'}
                      </Badge>
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    {summary.salesUploaded ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className={summary.salesUploaded ? '' : 'text-muted-foreground'}>
                      Sales
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {summary.payoutsUploaded ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className={summary.payoutsUploaded ? '' : 'text-muted-foreground'}>
                      Payouts
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {summary.quantitiesUploaded ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className={summary.quantitiesUploaded ? '' : 'text-muted-foreground'}>
                      Quantities
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {summary.itemsMapped}/{summary.totalItems} mapped
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onPrevious}>
          Previous Step
        </Button>
        <Button onClick={handleComplete} disabled={completing}>
          {completing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Completing...
            </>
          ) : (
            'Complete POS Training'
          )}
        </Button>
      </div>
    </div>
  );
};
